"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { vytvorProfilALogRegistraciu } from "@/lib/actions/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Copy, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

const AUTH_CARD_CLASSES =
  "w-full max-w-md border-muted/20 bg-transparent shadow-sm rounded-3xl overflow-hidden";
const AUTH_TITLE_CLASSES =
  "text-3xl font-black tracking-tighter uppercase text-foreground drop-shadow-sm";
const AUTH_SUBMIT_BUTTON_CLASSES = cn(
  "w-full rounded-full font-black uppercase tracking-widest text-xs h-12 shadow-md transition-all",
  "bg-primary text-primary-foreground"
);

function generovatSilneHeslo(): string {
  const velkePismena = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const malePismena = "abcdefghijklmnopqrstuvwxyz";
  const cisla = "0123456789";
  const specialne = "!@#$%^&*-_=+";
  const vsetky = velkePismena + malePismena + cisla + specialne;

  let heslo = "";
  heslo += velkePismena[Math.floor(Math.random() * velkePismena.length)];
  heslo += malePismena[Math.floor(Math.random() * malePismena.length)];
  heslo += cisla[Math.floor(Math.random() * cisla.length)];
  heslo += specialne[Math.floor(Math.random() * specialne.length)];

  for (let i = 0; i < 12; i++) {
    heslo += vsetky[Math.floor(Math.random() * vsetky.length)];
  }

  return heslo
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}

export function AuthForm() {
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") || "/";
  const defaultTab = searchParams.get("tab") || "prihlasenie";

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const router = useRouter();

  async function handlePrihlasenie(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error("Chyba pri prihlásení", {
        description: error.message,
      });
      setLoading(false);
      return;
    }

    toast.success("Úspešne prihlásený");
    setLoading(false);
    router.push(redirect);
    router.refresh();
  }

  async function handleRegistracia(e: React.FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Heslá sa nezhodujú");
      return;
    }

    if (password.length < 6) {
      toast.error("Heslo musí mať aspoň 6 znakov");
      return;
    }

    setLoading(true);

    const supabase = createClient();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) {
      toast.error("Chyba pri registrácii", {
        description: error.message,
      });
      setLoading(false);
      return;
    }

    if (data.user) {
      await vytvorProfilALogRegistraciu(data.user.id, email);

      toast.success("Účet vytvorený", {
        description: "Skontrolujte e-mail pre potvrdenie účtu.",
      });

      setActiveTab("prihlasenie");
      setPassword("");
      setConfirmPassword("");
      setLoading(false);
    }
  }

  function vlozitGenerovaneHeslo() {
    const noveheslo = generovatSilneHeslo();
    setPassword(noveheslo);
    setConfirmPassword(noveheslo);
    setShowPassword(true);
    setShowConfirmPassword(true);
    toast.success("Heslo vygenerované", {
      description: "Heslo bolo vložené do polí",
    });
  }

  async function kopirovatHeslo() {
    if (password) {
      await navigator.clipboard.writeText(password);
      toast.success("Heslo skopírované");
    }
  }

  return (
    <Card className={AUTH_CARD_CLASSES}>
      <CardHeader className="text-center space-y-2">
        <CardTitle className={AUTH_TITLE_CLASSES}>Autentifikácia</CardTitle>
        <CardDescription>
          Prihláste sa alebo si vytvorte účet pre hlasovanie
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="prihlasenie">Login</TabsTrigger>
            <TabsTrigger value="registracia">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="prihlasenie">
            <form onSubmit={handlePrihlasenie}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="login-email"
                    className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1"
                  >
                    Prihlasovací e-mail
                  </Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="vas@email.sk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 border-muted/20 bg-muted/5 focus:border-primary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="login-password"
                    className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1"
                  >
                    Prístupové heslo
                  </Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-12 border-muted/20 bg-muted/5 focus:border-primary/50 pr-12"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-12 px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <CardFooter className="flex flex-col gap-4 pt-6 pb-0 px-0">
                <Button
                  type="submit"
                  className={AUTH_SUBMIT_BUTTON_CLASSES}
                  disabled={loading}
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Prihlasujem...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
              </CardFooter>
            </form>
          </TabsContent>

          <TabsContent value="registracia">
            <form onSubmit={handleRegistracia}>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="register-email"
                    className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1"
                  >
                    Registračný e-mail
                  </Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="vas@email.sk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 border-muted/20 bg-muted/5 focus:border-primary/50"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="register-password"
                      className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1"
                    >
                      Prístupové heslo
                    </Label>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={vlozitGenerovaneHeslo}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Generovať
                      </Button>
                      {password && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={kopirovatHeslo}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Kopírovať
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="h-12 border-muted/20 bg-muted/5 focus:border-primary/50 pr-12"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-12 px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="register-confirm-password"
                    className="text-xs font-bold uppercase tracking-widest text-muted-foreground ml-1"
                  >
                    Potvrdenie hesla
                  </Label>
                  <div className="relative">
                    <Input
                      id="register-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-12 border-muted/20 bg-muted/5 focus:border-primary/50 pr-12"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-12 px-3 hover:bg-transparent"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              <CardFooter className="flex flex-col gap-4 pt-6 pb-0 px-0">
                <Button
                  type="submit"
                  className={AUTH_SUBMIT_BUTTON_CLASSES}
                  disabled={loading}
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Registrujem...
                    </>
                  ) : (
                    "Zaregistrovať sa"
                  )}
                </Button>
              </CardFooter>
            </form>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="text-sm text-muted-foreground text-center pb-8">
        Pokračovaním súhlasíte s podmienkami a pravidlami hlasovania
      </CardFooter>
    </Card>
  );
}
